import { useState } from "react";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { Copy, Check, FileDown, Loader2 } from "lucide-react";

const up = (s) => String(s ?? "").trim().toUpperCase();
const baseName = (n) => String(n || "").replace(/\s*\([^)]*\)\s*$/, "");

export const labelText = (o, shipFrom) => {
  const s = o.ship_to || {};
  const items = (o.line_items || []).map((l) => `${l.qty} x ${baseName(l.name)}${l.variant?.size ? ` / SIZE ${l.variant.size}` : ""}${l.variant?.color ? ` / ${l.variant.color}` : ""}`.toUpperCase());
  return [
    "FROM:", ...String(shipFrom || "PICKLE BULLPEN PRO SHOP").split("\n").map(up).filter(Boolean), "",
    "SHIP TO:", up(s.name), up(s.street), `${up(s.city)} ${up(s.state)} ${up(s.zip)}`.replace(/\s+/g, " ").trim(), "",
    `ORDER #${o.id.slice(-8).toUpperCase()} · ${(o.line_items || []).reduce((n, l) => n + l.qty, 0)} ITEM(S)`, ...items,
  ].join("\n");
};

function drawLabel(doc, o, shipFrom) {
  const s = o.ship_to || {};
  const W = 4, H = 6, M = 0.28;
  doc.setLineWidth(0.02); doc.rect(0.1, 0.1, W - 0.2, H - 0.2);
  doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(60); doc.text("FROM", M, M + 0.1, { charSpace: 0.03 });
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(0);
  const from = String(shipFrom || "PICKLE BULLPEN PRO SHOP").split("\n").map(up).filter(Boolean);
  let y = M + 0.3;
  from.forEach((ln) => { doc.text(ln, M, y); y += 0.16; });
  y += 0.12; doc.setLineWidth(0.03); doc.line(M, y, W - M, y); y += 0.3;
  doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(60); doc.text("SHIP TO", M, y, { charSpace: 0.03 }); y += 0.32;
  doc.setFontSize(17); doc.setTextColor(0);
  [up(s.name), up(s.street), `${up(s.city)} ${up(s.state)} ${up(s.zip)}`.replace(/\s+/g, " ").trim()].forEach((ln) => {
    doc.splitTextToSize(ln, W - 2 * M).forEach((part) => { doc.text(part, M, y); y += 0.3; });
  });
  const items = o.line_items || [];
  const count = items.reduce((n, l) => n + l.qty, 0);
  let fy = H - M - 0.2 - items.length * 0.17 - 0.45;
  doc.setLineWidth(0.01); doc.line(M, fy, W - M, fy); fy += 0.2;
  doc.setFontSize(8); doc.setFont("helvetica", "bold");
  doc.text(`ORDER #${o.id.slice(-8).toUpperCase()}  ·  ${String(o.created_at || "").slice(0, 10)}  ·  ${count} ITEM(S)`, M, fy); fy += 0.2;
  doc.setFontSize(6); doc.setTextColor(90);
  doc.text("QTY", M, fy); doc.text("ITEM", M + 0.35, fy); doc.text("SIZE", W - M - 1.05, fy); doc.text("COLOR", W - M - 0.6, fy); fy += 0.14;
  doc.setTextColor(0); doc.setFontSize(8);
  items.forEach((l) => {
    doc.setFont("helvetica", "normal");
    doc.text(`${l.qty}×`, M, fy);
    doc.text(doc.splitTextToSize(baseName(l.name), 1.95)[0], M + 0.35, fy);
    doc.setFont("helvetica", "bold");
    doc.text(up(l.variant?.size || "—"), W - M - 1.05, fy);
    doc.text(up(l.variant?.color || "—"), W - M - 0.6, fy);
    fy += 0.17;
  });
  doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(90);
  doc.text("PICKLE BULLPEN PRO SHOP · 941-899-5300", M, H - M + 0.05);
}

export async function downloadLabelsPdf(orders, shipFrom, filename) {
  const doc = new jsPDF({ unit: "in", format: [4, 6], orientation: "portrait" });
  orders.forEach((o, i) => { if (i > 0) doc.addPage([4, 6], "portrait"); drawLabel(doc, o, shipFrom); });
  doc.save(filename);
}

export default function ShippingLabel({ o, shipFrom }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const text = labelText(o, shipFrom);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); toast.success("Label copied — paste into USPS / UPS"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Clipboard blocked — select the text and copy manually"); }
  };
  const pdf = async () => {
    setBusy(true);
    try { await downloadLabelsPdf([o], shipFrom, `label_${o.id.slice(-8)}_${(o.ship_to?.name || "order").replace(/[^a-z0-9]+/gi, "_")}.pdf`); }
    catch { toast.error("Could not build the PDF"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 border border-pb-border/60 bg-black/40" data-testid={`order-label-${o.id}`}>
      <div className="flex items-center justify-between gap-2 px-2.5 py-1 border-b border-pb-border/60">
        <span className="text-[9px] font-black uppercase tracking-[0.25em] text-zinc-400">Shipping Label · USPS / UPS ready</span>
        <div className="flex gap-1.5">
          <button data-testid={`order-label-copy-${o.id}`} onClick={copy} className="flex items-center gap-1 border border-pb-border hover:border-white text-zinc-300 hover:text-white px-2 py-1 text-[9px] font-black uppercase tracking-widest transition-colors">
            {copied ? <Check className="w-3 h-3 text-[#A3E635]" /> : <Copy className="w-3 h-3" />} {copied ? "Copied" : "Copy Label"}
          </button>
          <button data-testid={`order-label-pdf-${o.id}`} onClick={pdf} disabled={busy} className="flex items-center gap-1 bg-white text-black hover:bg-[#BEF264] px-2 py-1 text-[9px] font-black uppercase tracking-widest transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />} Label PDF (4×6)
          </button>
        </div>
      </div>
      <pre className="px-3 py-2 font-mono text-[11px] leading-snug text-zinc-200 whitespace-pre-wrap select-all" data-testid={`order-label-text-${o.id}`}>{text}</pre>
    </div>
  );
}
